import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

const MultiSelect = ({
  options = [],
  handleAddOption,
  placeholder = 'Selecione uma opção',
  disabled = false,
  emptyStateMessage = 'Nenhum deck encontrado',
}: {
  options: { uuid: string; title: string }[];
  handleAddOption: (optionUuid: string) => void;
  placeholder?: string;
  disabled?: boolean;
  emptyStateMessage?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    openAbove: false,
  });
  const multiselectRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const openTimestampRef = useRef<number>(0);

  useEffect(() => {
    if (!search.trim()) {
      setFilteredOptions(options);
    } else {
      const lower = search.toLowerCase();
      setFilteredOptions(
        options.filter((opt) => opt.title.toLowerCase().includes(lower))
      );
    }
  }, [search, options]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearch('');
    // Remove o foco do input ao fechar, garantindo que funcione em navegadores e dispositivos móveis (Android/iOS)
    inputRef.current?.blur();
  }, []);

  // Handle clicks outside to close dropdown (works better on iOS than blur)
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      // Ignore events that happen too quickly after opening (iOS touch event propagation)
      if (Date.now() - openTimestampRef.current < 300) {
        return;
      }

      const target = e.target as Node;

      if (
        containerRef.current?.contains(target) ||
        multiselectRef.current?.contains(target)
      ) {
        return;
      }

      closeDropdown();
    };

    // Use touchstart for iOS compatibility (touchend can have timing issues)
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, closeDropdown]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    if (!disabled && !isOpen) {
      openTimestampRef.current = Date.now();
      setIsOpen(true);
    }
  };

  const handleFocus = () => {
    if (!disabled) {
      openTimestampRef.current = Date.now();
      setIsOpen(true);
    }
  };

  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const dropdownHeight = 160; // max-h-40 = 160px
      const spaceBelow = window.innerHeight - rect.bottom;
      const openAbove =
        spaceBelow < dropdownHeight && rect.top > dropdownHeight;

      setDropdownPosition({
        top: openAbove
          ? rect.top + window.scrollY - dropdownHeight - 4
          : rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        openAbove,
      });
    }
  }, [isOpen]);

  const handleSelectOption = (optionUuid: string) => {
    handleAddOption(optionUuid);
    closeDropdown();
  };

  return (
    <div ref={containerRef} className='relative inline-block w-full'>
      <div
        tabIndex={0}
        className={`relative ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <input
          ref={inputRef}
          type='text'
          disabled={disabled}
          placeholder={placeholder}
          value={search}
          onChange={handleInputChange}
          onFocus={handleFocus}
          className={`border flex w-full appearance-none items-center overflow-hidden rounded-md border-mono-color-light focus:outline-none ${
            disabled ? 'bg-gray-100' : ''
          }`}
        />
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={multiselectRef}
            tabIndex={-1}
            style={{
              position: 'absolute',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
            }}
            className={`border z-[9999] max-h-40 gap-2 overflow-y-auto rounded-md border-hairline border-mono-color-light bg-card-color-default shadow-level-1 ${dropdownPosition.openAbove ? 'mb-1' : 'mt-1'}`}
          >
            {filteredOptions.length === 0 && (
              <div className='flex h-[80px] items-center justify-center p-2'>
                <span className='text-sm italic text-color-brand-primary'>
                  {emptyStateMessage}
                </span>
              </div>
            )}
            {filteredOptions.map((option) => (
              <div
                key={option.uuid}
                role='option'
                className='flex cursor-pointer items-center gap-2 p-2 hover:bg-card-color-negative hover:text-card-color-default active:bg-card-color-negative'
                onClick={() => handleSelectOption(option.uuid)}
                onTouchStart={(e) => {
                  const touch = e.touches[0];
                  touchStartRef.current = {
                    x: touch.clientX,
                    y: touch.clientY,
                  };
                }}
                onTouchEnd={(e) => {
                  if (!touchStartRef.current) return;

                  const touch = e.changedTouches[0];
                  const deltaX = Math.abs(
                    touch.clientX - touchStartRef.current.x
                  );
                  const deltaY = Math.abs(
                    touch.clientY - touchStartRef.current.y
                  );

                  // Only select if it was a tap (minimal movement), not a scroll
                  if (deltaX < 10 && deltaY < 10) {
                    e.preventDefault();
                    handleSelectOption(option.uuid);
                  }

                  touchStartRef.current = null;
                }}
              >
                {option.title}
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

export default MultiSelect;
